import { useToast } from "components/atoms/toast/toast-context";
import { useGetCoursesById } from "hooks/use-get-courses-by-ids/use-get-courses-by-ids";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import trackEvent from "services/mixpanel/mixpanel";
import { slotSelectionEventNames } from "services/mixpanel/slot-selection-events";
import {
  ISlotType,
  IUseModalSlotConfirmation,
  ModalSlotConfirmationProps
} from "./modal-slot-confirmation.types";

const useModalSlotConfirmation = ({
  isOpen,
  onFinish,
  sections
}: ModalSlotConfirmationProps): IUseModalSlotConfirmation => {
  const router = useRouter();
  const { userSubscriptionId } = router.query;
  const [loading, setLoading] = useState(false);
  const { selectSlots } = useGetCoursesById({ router });
  const { addToast } = useToast();

  useEffect(() => {
    if (isOpen && sections) {
      const { courseIdIncluded, courseNameIncluded } = sections.reduce(
        (result, section) => {
          result.courseIdIncluded.push(section.course_id);
          result.courseNameIncluded.push(section.title);
          return result;
        },
        { courseIdIncluded: [] as string[], courseNameIncluded: [] as string[] }
      );
      const mixpanelPayload = {
        courseIdIncluded,
        courseNameIncluded,
        totalCourseCount: getCourseDataLength(ISlotType.ALL),
        mandatoryCourseCount: getCourseDataLength(ISlotType.MANDATORY),
        extraCourseCount: getCourseDataLength(ISlotType.EXTRA),
        withTeacherName: !!sections.find(
          (section) => section.selected_slot?.slot.teacher?.name
        )
      };
      trackEvent({
        eventName: slotSelectionEventNames.scheduleConfirmationViewed,
        payload: mixpanelPayload
      });
    }
  }, [isOpen, sections]);

  const handleSubmit = async () => {
    setLoading(true);
    const mixpanelPayload = {
      totalCourseCount: getCourseDataLength(ISlotType.ALL),
      mandatoryCourseCount: getCourseDataLength(ISlotType.MANDATORY),
      extraCourseCount: getCourseDataLength(ISlotType.EXTRA),
      withTeacherName: !!sections.find(
        (section) => section.selected_slot?.slot.teacher?.name
      )
    };
    trackEvent({
      eventName: slotSelectionEventNames.scheduleConfirmed,
      payload: mixpanelPayload
    });

    const payload = {
      slotIds: sections
        .map((section) => section.selected_slot?.slot.id || "")
        .filter((id) => id),
      userSubscriptionId: String(userSubscriptionId)
    };
    const { error } = await selectSlots(payload);
    if (!error) {
      router.push("/bimbel");
      onFinish();
    } else {
      addToast({
        heading: "Yah, gagal memilih jadwal. Coba lagi nanti",
        variant: "error"
      });
    }

    setLoading(false);
  };

  const getCourseDataLength = (type: ISlotType) => {
    const comparison = (isOptional: boolean) =>
      type === ISlotType.ALL
        ? true
        : type === ISlotType.MANDATORY
        ? !isOptional
        : isOptional;
    return sections.filter(
      (section) =>
        section.selected_slot?.slot && comparison(section.is_optional)
    ).length;
  };

  return { handleSubmit, loading };
};

export default useModalSlotConfirmation;
